/* Mobeus chat panel — shadow DOM reset + theme tokens.
 *
 * Bundled as a text string into embed.js (esbuild `text` loader) and
 * injected into the shadow root by the shadow render adapter.
 *
 * `:host { all: initial }` resets every inherited property on the host
 * <div>, so a customer page with `body { font-family: ComicSans }` or
 * `* { box-sizing: content-box }` cannot leak into the chat panel. Note:
 * `all` deliberately does NOT reset custom properties, so the `--mobeus-*`
 * design tokens below survive the reset and inherit into the panel.
 *
 * THEMING
 * -------
 * The presentation components consume `--mobeus-*` tokens via
 * `var(--mobeus-x, <fallback>)`. This file defines the light palette by
 * default and swaps to a dark palette when the widget is in dark mode:
 *   - explicit dark:  host carries `data-mobeus-scheme="dark"`
 *   - auto (follow OS): host carries `data-mobeus-scheme="auto"` and the
 *     host page is in `prefers-color-scheme: dark`
 * The render adapter stamps `data-mobeus-scheme` from `styling.colorScheme`.
 *
 * `color-scheme` is pinned to the resolved mode so that unstyled form
 * controls / system canvas colours match the palette instead of flipping to
 * the OS appearance (the Safari dark-mode bug this file originally shipped).
 *
 * CUSTOMISATION (see config `styling`)
 *   - `styling.vars`  → `--mobeus-*` overrides, applied inline on the host.
 *   - `styling.css`   → raw CSS appended after this sheet; can target the
 *                       stable `.mobeus-*` element classes.
 *   - `::part(...)`   → every themable element exposes a part name, so the
 *                       host page's own stylesheet can reach in too.
 *
 * The iframe adapter does NOT use this file — it loads its own presentation
 * page over a network boundary. Components keep light-value fallbacks in
 * their `var(...)` calls so iframe mode renders unchanged.
 */
:host {
  all: initial;

  /* ---- light palette (default) ---- */
  --mobeus-font: system-ui, sans-serif;
  --mobeus-surface: #ffffff;
  --mobeus-surface-muted: #f3f4f6;
  --mobeus-text: #111827;
  --mobeus-text-muted: #6b7280;
  --mobeus-text-faint: #9ca3af;
  --mobeus-border: #e5e7eb;
  --mobeus-input-bg: #ffffff;
  --mobeus-input-border: #d1d5db;
  --mobeus-primary: #3b82f6;
  --mobeus-primary-text: #ffffff;
  --mobeus-muted-btn: #6b7280;
  --mobeus-danger: #ef4444;
  --mobeus-agent-bubble: #f3f4f6;
  --mobeus-agent-bubble-text: #111111;
  --mobeus-user-bubble: #3b82f6;
  --mobeus-user-bubble-text: #ffffff;
  --mobeus-chip-bg: #dbeafe;
  --mobeus-chip-border: #93c5fd;
  --mobeus-chip-text: #1e40af;
  --mobeus-radius: 12px;
  --mobeus-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);

  /* Color grading applied to the idle background image and avatar video
   * layers (Phase 2 — avatar/background parity), ported from the hosted
   * site's `--theme-video-brightness`/`--theme-video-saturate` tokens. */
  --mobeus-avatar-brightness: 0.72;
  --mobeus-avatar-saturate: 0.9;

  /* Which part of the avatar video survives the `object-fit:cover` crop.
   * Avatar streams are 16:9 (1280x720), but a docked/fullscreen panel is
   * often portrait (e.g. 480x720), so cover keeps only a 480px-wide slice.
   * `center` drops any subject that isn't centered in the source frame —
   * an avatar composed off to one side then crops out entirely and the
   * panel shows nothing but backdrop. Partners whose avatar is framed
   * off-center override this via `styling.vars` (e.g.
   * `{"avatar-position":"right center"}`). */
  --mobeus-avatar-position: center;

  /* ---- fullscreen takeover tokens (Phase 6) ----
   * `FullscreenPanel.tsx` is ALWAYS a dark overlay on top of full-bleed
   * avatar video, regardless of `styling.colorScheme` (which only controls
   * the compact panel's light/white vs dark surface). So these are separate,
   * constant tokens rather than reusing `--mobeus-text`/`--mobeus-surface`
   * etc — those flip to dark-on-light values under `colorScheme:"light"`
   * (the default), which would make fullscreen's text unreadable against
   * its own dark video background. Values match momentum.mobeus.com's live
   * DOM (reverse-engineered during Phase 2's `styling` work), still
   * overridable per-partner via `styling.vars` (e.g. `{"fs-primary":"#..."}`
   * — same `--mobeus-<key>` mechanism as every other token here). */
  --mobeus-fs-text: #e7ecf3;
  --mobeus-fs-text-muted: #9aa6b6;
  --mobeus-fs-primary: #1bb8a6;
  --mobeus-fs-danger: #f87171;
  /* Momentum's measured chat-message-bubble glass. */
  --mobeus-fs-bubble-bg: rgba(255, 255, 255, 0.12);
  --mobeus-fs-bubble-border: rgba(255, 255, 255, 0.1);
  --mobeus-fs-glass-bg: rgba(255, 255, 255, 0.1);
  --mobeus-fs-glass-border: rgba(255, 255, 255, 0.12);

  color-scheme: light;
}

/* ---- dark palette ---- */
:host([data-mobeus-scheme='dark']) {
  --mobeus-surface: #1f2937;
  --mobeus-surface-muted: #374151;
  --mobeus-text: #f9fafb;
  --mobeus-text-muted: #9ca3af;
  --mobeus-text-faint: #6b7280;
  --mobeus-border: #374151;
  --mobeus-input-bg: #111827;
  --mobeus-input-border: #4b5563;
  --mobeus-muted-btn: #4b5563;
  --mobeus-agent-bubble: #374151;
  --mobeus-agent-bubble-text: #f9fafb;
  --mobeus-chip-bg: #1e3a8a;
  --mobeus-chip-border: #3b82f6;
  --mobeus-chip-text: #bfdbfe;
  --mobeus-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);

  color-scheme: dark;
}

@media (prefers-color-scheme: dark) {
  :host([data-mobeus-scheme='auto']) {
    --mobeus-surface: #1f2937;
    --mobeus-surface-muted: #374151;
    --mobeus-text: #f9fafb;
    --mobeus-text-muted: #9ca3af;
    --mobeus-text-faint: #6b7280;
    --mobeus-border: #374151;
    --mobeus-input-bg: #111827;
    --mobeus-input-border: #4b5563;
    --mobeus-muted-btn: #4b5563;
    --mobeus-agent-bubble: #374151;
    --mobeus-agent-bubble-text: #f9fafb;
    --mobeus-chip-bg: #1e3a8a;
    --mobeus-chip-border: #3b82f6;
    --mobeus-chip-text: #bfdbfe;
    --mobeus-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);

    color-scheme: dark;
  }
}

:host * {
  box-sizing: border-box;
}

/* ══════════════════════════════════════════════════════════════
 * ANIMATION INFRASTRUCTURE (Phase 3 — reusable keyframes/classes)
 * ══════════════════════════════════════════════════════════════
 * Ported from the hosted site's `globals.css` (`chat-bubble-enter`,
 * `connecting-pulse`, `hero-pulse`) with values matched and tokens renamed
 * to the `--mobeus-*` scheme. Loaded in both render modes (see file header).
 */

/* ---- message/bubble entrance ----
 * Reusable class + keyframe only — NOT applied anywhere in this phase.
 * Phase 5 owns wiring per-message stagger delay in Transcripts.tsx; this
 * just needs to exist and be visually correct in isolation. */
@keyframes mobeus-chat-bubble-enter {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.mobeus-bubble-enter {
  animation: mobeus-chat-bubble-enter 0.4s ease-out forwards;
}

/* ---- connect-button pulse (ring/box-shadow expanding) ----
 * Applied by Controls.tsx to the Connect button while a connection attempt
 * is in flight (`isConnecting`). Color follows the live `--mobeus-primary`
 * token (including partner `styling.vars` overrides) via `color-mix()`. */
@keyframes mobeus-connecting-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--mobeus-primary, #3b82f6) 40%, transparent);
  }
  70% {
    box-shadow: 0 0 0 12px transparent;
  }
  100% {
    box-shadow: 0 0 0 0 transparent;
  }
}
.mobeus-connecting-pulse {
  animation: mobeus-connecting-pulse 1.5s ease-in-out infinite;
}

/* The avatar <video> is created imperatively by builtins/avatar.ts, which
 * sets `object-fit` inline but deliberately leaves `object-position` to CSS
 * so partners can retarget the crop without a bundle change. */
video[data-mobeus-avatar] {
  object-position: var(--mobeus-avatar-position, center);
}

/* ---- agent-thinking glow (gentle opacity oscillation, not a spinner) ----
 * Applied by ChatPanel.tsx to the idle-background/avatar-video layers when
 * `agentState === 'thinking'`, alongside their existing Phase 2 crossfade
 * transition styles. */
@keyframes mobeus-thinking-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
}
.mobeus-thinking-pulse {
  animation: mobeus-thinking-pulse 2s ease-in-out infinite;
}

/* ---- bubble↔panel swap: panel content entrance ----
 * Applied unconditionally to the expanded panel's root `<div>` in
 * ChatPanel.tsx. Since the bubble↔panel swap is a hard conditional return,
 * the panel `<div>` is a fresh DOM node every time `isOverlayExpanded`
 * flips true, so this animation replays on every open — no JS-driven
 * mount-tracking needed. Same easing as the bubble-enter keyframe above,
 * scaled down (no translateY — the panel opens in place, it doesn't slide
 * up) and slightly shorter since it's a larger, more central element. */
@keyframes mobeus-panel-enter {
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
.mobeus-panel-enter {
  animation: mobeus-panel-enter 0.25s ease-out forwards;
}

/* ══════════════════════════════════════════════════════════════
 * PHASE 6 — fullscreen takeover additions
 * ══════════════════════════════════════════════════════════════
 * `mobeus-panel-enter` (above) is reused as-is for the fullscreen root's
 * entrance — same swap-triggered mount, same effect works fine at full
 * viewport size. The only genuinely new visual need is this chevron
 * rotation on `FullscreenPanel.tsx`'s expandable tool-call chips, which
 * inline styles can't express as a transition. Easing matches momentum's
 * own `--membrane-ease` token (micro-interaction, not a positional shift). */
.mobeus-fs-chip-chevron {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.mobeus-fs-chip-chevron.mobeus-open {
  transform: rotate(180deg);
}

/* ---- avatar "alive" animations, ported from momentum ----
 * `avatar-video-pulse`: the avatar's brightness breathes while the agent is
 * thinking or speaking (the keyframes must restate the full grading filter
 * because `filter` isn't additive — same approach momentum takes with its
 * --m-brightness var). `avatar-thinking-glow`: a teal radial glow breathing
 * over the avatar while thinking. Both 1.8s with momentum's exact easing. */
@keyframes mobeus-fs-avatar-pulse {
  0%,
  100% {
    filter: brightness(var(--mobeus-avatar-brightness, 0.72)) saturate(var(--mobeus-avatar-saturate, 0.9));
  }
  50% {
    filter: brightness(calc(var(--mobeus-avatar-brightness, 0.72) * 1.12)) saturate(var(--mobeus-avatar-saturate, 0.9));
  }
}
.mobeus-fs-avatar-pulse {
  animation: mobeus-fs-avatar-pulse 1.8s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}
@keyframes mobeus-fs-avatar-breathe {
  0%,
  100% {
    opacity: 0.05;
  }
  50% {
    opacity: 0.35;
  }
}
.mobeus-fs-thinking-glow {
  background: radial-gradient(
    70% 90% at 65% 45%,
    rgba(129, 216, 208, 0.55) 0,
    rgba(129, 216, 208, 0.25) 35%,
    rgba(255, 255, 255, 0.1) 60%,
    transparent 80%
  );
  animation: mobeus-fs-avatar-breathe 1.8s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

/* ---- inline status bubble dot (momentum's "Looking…" indicator) ---- */
@keyframes mobeus-fs-dot-pulse {
  0%,
  100% {
    opacity: 0.12;
  }
  50% {
    opacity: 0.9;
  }
}
.mobeus-fs-status-dot {
  animation: mobeus-fs-dot-pulse 1.3s ease-in-out infinite;
}

/* Momentum hides the chat scrollbar entirely (scrollbar-width: none is set
 * inline on the history element; this covers WebKit). */
.mobeus-fs-history::-webkit-scrollbar {
  display: none;
}
